import {
  initAnalytics,
  setUserId,
  setUserProperties,
  useAnalytics,
} from '@cw-elements/analytics-provider';
import { useCallback } from 'react';
import { plugin as engagementPlugin } from '@amplitude/engagement-browser';
import i18n from './localization/config';

export const convertI18nToAmplitudeLocale = (i18nLanguage: string): string => {
  const [lang, region] = i18nLanguage.split('_');
  return region ? `${lang}-${region.toUpperCase()}` : lang;
};

export enum AmplitudeEventEnum {
  ACCESS_FEATURE = 'access feature',
  ACCESS_INTEGRATION_TYPE = 'access integration',
  CONNECTED_NEW_INTEGRATION = 'successfully connected new integration',
  EDIT_INTEGRATION = 'edit integration',
  EDIT_INTEGRATION_SUCCESS = 'successfully edited integration',
  RETURN_FROM_INTEGRATION = 'return from integration',
  SELECT_INTEGRATION_TYPE = 'select integration type',
  SET_UP_INTEGRATION = 'set up integration',
  SELECT_PRODUCT_TO_MATCH = 'select product to match',
  FILTER_UNMAPPED_PRODUCT_LIST = 'filter unmapped product list',
  OPEN_OPERATION_DETAILS = 'open operation details',
  IGNORE_PRODUCT = 'ignore product',
  SELECT_CATALOG_PRODUCT = 'select catalog product',
  MATCH_PRODUCT = 'match product',
  SUCCESSFULLY_MATCH_PRODUCT = 'successfully match product',
  ACCESS_MATCHING_HISTORY = 'access matching history',
  LOAD_MORE_MATCHED_PRODUCTS = 'load more matched products',
  FILTER_MATCHING_HISTORY = 'filter matching history',
  UNMATCH_PRODUCT = 'unmatch product',
  RETURN_FROM_MATCHING_HISTORY = 'return from matching history',
}

export enum AmplitudePropertyKeyEnum {
  APPLIED_TO_FUTURE_OPS = 'applied to future operations',
  FEATURE = 'feature',
  INTEGRATION_NAME = 'integration name',
  INTEGRATION_TYPE = 'integration type',
  FILTER_ACTION = 'filter action',
  SUGGESTED_PRODUCT = 'suggested product',
  OPERATIONS_SELECTED = 'operations selected',
}

export const initializeAmplitude = (clientId: string, enableGuides = true) => {
  // Skip Amplitude initialization during Cypress tests
  if ((globalThis as any).Cypress || import.meta.env.CYPRESS_INSTRUMENT_PRODUCTION === 'true') {
    return;
  }

  initAnalytics({ clientId })
    .then((analyticsInstance) => {
      if (enableGuides) {
        analyticsInstance.add(
          engagementPlugin({ locale: convertI18nToAmplitudeLocale(i18n.language) }),
        );
      }
    })
    .catch((error) => {
      console.error('Failed to initialize analytics:', error);
    });
};

export const useAmplitude = () => {
  const trackEventFunction = useAnalytics();
  return useCallback(
    (
      eventType: AmplitudeEventEnum,
      eventProperties?: {
        [key in AmplitudePropertyKeyEnum]?: unknown;
      },
    ) => {
      trackEventFunction(eventType, eventProperties);
    },
    [trackEventFunction],
  );
};

const setAmplitudeUserId = (userId: string) => {
  setUserId(userId);
};

const setAmplitudeUserProperties = (userProperties: { [key: string]: string }) => {
  setUserProperties(userProperties);
};

export const setAmplitudeUserDetails = (user: any) => {
  setAmplitudeUserId(user?.id);
  setAmplitudeUserProperties({ 'job title': user?.job_title });
};

export const setAmplitudeOrganizationDetails = (org: any) => {
  setAmplitudeUserProperties({
    workspace_name: org?.workspace_name,
    workspace_id: org?.workspace_id,
    organization_name: org?.name,
    organization_id: org?.id,
  });
};
